import { Injectable } from "@angular/core";
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { catchError, filter, map, Observable, of, switchMap, take } from "rxjs";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { IApplicationContextService } from "../services/iApplication-context.service";
import { ICurrentUserService } from "../services/currentUserService/icurrentuser.service";
import { User, UserClient } from "../services/nswag/nswagclient";
import { TelemetryService } from "../services/telemetry.service";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AccountInfo, InteractionStatus } from "@azure/msal-browser";
import { getOpsViewerScopes } from "../services/currentUserService/auth-scopes";

@Injectable()
export class HasCompanyGuard {
    constructor(private _applicationContextService: IApplicationContextService,
        private _router: Router,
        private _currentUserService: ICurrentUserService,
        private _userClient: UserClient,
        private _telemetry: TelemetryService,
        private _msalBroadcastService: MsalBroadcastService,
        private _msalService: MsalService
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this._msalBroadcastService.inProgress$.pipe(
            filter(status => status === InteractionStatus.None),
            take(1),
            switchMap(() => this.checkCompanyAccess(state))
        );
    }

    private checkCompanyAccess(state: RouterStateSnapshot): Observable<boolean> {
        const email = this.getResolvedEmail();
        if (!email) {
            const authTelemetryProperties = this.getAuthTelemetryProperties();
            if (this._msalService.instance.getAllAccounts().length === 0) {
                this._telemetry.trackTrace({
                    message: `HasCompanyGuard: no MSAL account available after auth settled; starting login instead of redirecting to /nocompany from ${state.url}`,
                    severityLevel: SeverityLevel.Warning
                }, authTelemetryProperties);
                this._msalService.loginRedirect({ scopes: getOpsViewerScopes(), redirectStartPage: state.url });
                return of(false);
            }

            this._telemetry.trackTrace({
                message: `HasCompanyGuard: no email available after auth settled; redirecting to /nocompany from ${state.url}`,
                severityLevel: SeverityLevel.Warning
            }, authTelemetryProperties);
            this._router.navigate(['/nocompany']);
            return of(false);
        }

        return this._userClient.getByEmail(email).pipe(
            map(u => {
                if (this.hasAccess(u)) {
                    return true;
                }
                this._telemetry.trackTrace({
                    message: `HasCompanyGuard: ${this.getAccessFailureReason(u)}; redirecting to /nocompany from ${state.url}`,
                    severityLevel: SeverityLevel.Warning
                }, { email, userFound: String(!!u), userCompanyRoleCount: String(u?.companyRoles?.length ?? 0) });
                this._router.navigate(['/nocompany']);
                return false;
            }),
            catchError(err => {
                // Don't strand the user on /nocompany due to a transient lookup failure.
                this._telemetry.trackTrace({
                    message: `HasCompanyGuard: getByEmail failed; allowing navigation to ${state.url}. ${err?.message ?? ''}`,
                    severityLevel: SeverityLevel.Error
                }, { email });
                return of(true);
            })
        );
    }

    private getResolvedEmail(): string | null {
        const currentUserEmail = this._currentUserService.getUserEmail();
        if (currentUserEmail) {
            return currentUserEmail;
        }

        const account = this._currentUserService.getUser()
            ?? this._msalService.instance.getActiveAccount()
            ?? this._msalService.instance.getAllAccounts()[0]
            ?? null;

        return this.getAccountEmail(account);
    }

    private getAccountEmail(account: AccountInfo | null): string | null {
        if (!account) {
            return null;
        }

        const claims = account.idTokenClaims as Record<string, any> || {};
        return (Array.isArray(claims['emails']) && claims['emails'][0])
            || claims['email']
            || claims['preferred_username']
            || claims['upn']
            || claims['login_hint']
            || claims['signInNames.emailAddress']
            || claims['signInNames.email']
            || claims['signInNames']?.emailAddress
            || account.username
            || null;
    }

    private getAuthTelemetryProperties(): Record<string, string> {
        const accounts = this._msalService.instance.getAllAccounts();
        const account = this._currentUserService.getUser()
            ?? this._msalService.instance.getActiveAccount()
            ?? accounts[0]
            ?? null;

        return {
            activeAccountPresent: String(!!this._msalService.instance.getActiveAccount()),
            allAccountCount: String(accounts.length),
            allAccountUsernames: accounts.map(a => a.username || 'No username').join(','),
            allAccountEnvironments: accounts.map(a => a.environment || 'No environment').join(','),
            selectedAccountClaimKeys: Object.keys((account?.idTokenClaims as Record<string, unknown>) ?? {}).join(','),
            selectedAccountEmailCandidates: this.getEmailCandidates(account).join(',')
        };
    }

    private getEmailCandidates(account: AccountInfo | null): string[] {
        if (!account) {
            return [];
        }

        const claims = account.idTokenClaims as Record<string, any> || {};
        return [
            ...(Array.isArray(claims['emails']) ? claims['emails'] : []),
            claims['email'],
            claims['preferred_username'],
            claims['upn'],
            claims['login_hint'],
            claims['signInNames.emailAddress'],
            claims['signInNames.email'],
            claims['signInNames']?.emailAddress,
            account.username
        ].filter((value): value is string => typeof value === 'string' && value.length > 0);
    }

    private hasAccess(user: User | null | undefined): boolean {
        return !!user && !!(user.isGlobalAdmin || user.hasAllCompany || (user.companyRoles && user.companyRoles.length > 0));
    }

    private getAccessFailureReason(user: User | null | undefined): string {
        if (!user) { return 'user record was not found'; }
        return 'user has no permissions';
    }
}